/**
 * Optimistic Updater Service
 *
 * Handles optimistic UI updates for drawing designation changes.
 * Updates tree nodes with new designation values before server confirmation.
 */

import { hasDelimiterColon, stripDesignationFromText } from './textParser'

/**
 * Content item view node structure
 */
export interface ContentItemViewNode {
    id?: string
    dtvItemId?: string
    elements?: Array<{
        id?: string
        text?: string
        [key: string]: unknown
    }>
    [key: string]: unknown
}

/**
 * Creates an optimistic update for a drawing designation change.
 *
 * Strips the old designation from element text and updates the
 * `drawingDesignation` field — the single source of truth for
 * the designation value. The display layer reads `drawingDesignation`
 * directly via `DesignationField`, so the text no longer needs to
 * carry an embedded copy.
 *
 * @param nodes - Array of content item view nodes
 * @param targetContentItemId - ID of the content item to update
 * @param newDesignation - New designation value
 * @param currentDesignation - Current designation value from server, used for
 *   precise stripping when the text contains parentheses
 * @returns Updated nodes array with optimistic changes
 */
export const createOptimisticDesignationUpdate = (
    nodes: ContentItemViewNode[],
    targetContentItemId: string,
    newDesignation: string,
    currentDesignation?: string
): ContentItemViewNode[] => {
    return nodes.map((node) => {
        // Check if this node contains the target element
        const hasTargetElement = node.elements?.some(
            (element) => element.id === targetContentItemId
        )

        if (!hasTargetElement) {
            return node
        }

        // Strip the old designation from the matching element's text.
        // The designation value lives on the drawingDesignation field (updated below),
        // so we only need to remove the old embedded copy from the text string.
        const updatedElements = node.elements!.map((element) => {
            if (element.id !== targetContentItemId) {
                return element
            }

            return {
                ...element,
                text: stripDesignationFromText(element.text, currentDesignation),
            }
        })

        // Also strip from top-level text to stay consistent with elements.
        // TreeDataMapper assigns contentItemView directly from these nodes, and
        // intermediate re-renders (triggered by editorDeactivationSignal) can read
        // the top-level text before the tree fully rebuilds from elements.
        // Only strip if the text actually contains the delimiter colon — guards
        // against silently corrupting nodes whose text doesn't match the expected format.
        const updatedText =
            typeof node.text === 'string' && hasDelimiterColon(node.text)
                ? stripDesignationFromText(node.text, currentDesignation)
                : node.text

        return {
            ...node,
            elements: updatedElements,
            text: updatedText,
            // drawingDesignation is the single source of truth for the designation value
            drawingDesignation: newDesignation.trim() || '',
        }
    })
}
